// packages/core/src/quizzes/content/retirementReality.ts
// ─────────────────────────────────────────────────────────────────────────
// "The Retirement Age Reality Check" (estimate engine)
//
// Six estimation questions about made-up people with fully stated
// numbers. Every answer computed at a 5% return after inflation with a
// safe 4% yearly spend (target = 25 × yearly spending), monthly
// compounding, verified in-session:
//
//   Q1  Person A: 30, $0, $1,500/mo, spends $45,000/yr → age 58.4
//   Q2  A cuts spending $500/mo (saves $2,000/mo, needs less) → 6.2y earlier
//   Q3  Person B: 45, $150,000, $2,000/mo, spends $50,000/yr → age 65.2
//   Q4  A at a 4% return instead of 5% → 3.0y later
//   Q5  A saves an extra $250/mo (spending unchanged) → 2.2y earlier
//   Q6  Person C: $2,000/mo from 30 to 40, then stops adding, spends
//       $45,000/yr → balance $310,565 at 40, reaches target at age 66.4
//
// The Q2 vs Q5 pair is the quiz's core lesson: a $500 spending cut moves
// the date almost 3 times as far as $250 of extra saving, because a
// spending cut works both sides (more saved AND a smaller target).
// ─────────────────────────────────────────────────────────────────────────

import type { EstimateQuizContent } from '../DiscoveryTypes';

export const RETIREMENT_REALITY: EstimateQuizContent = {
	meta: {
		id: 'retirement-reality',
		title: 'The Retirement Age Reality Check',
		hook: 'When does work become optional? See what actually moves that date.',
		engine: 'estimate',
		minutes: 4,
		status: 'live',
	},

	questions: [
		{
			type: 'estimate',
			id: 'rr-base-age',
			setup: 'Person A is 30 with nothing invested. They invest $1,500 each month at a 5% return after inflation, and they spend $45,000 a year. Assume work becomes optional when their investment reaches 25 times their yearly spending.',
			prompt: 'At what age can Person A stop working?',
			unit: 'years',
			answer: 58.4,
			goodWithin: 1.08,
			zeroBeyond: 1.35,
			bias: 'rate_leverage_blindspot',
			biasDirection: 'over',
			reveal: 'Age 58. 25 times their yearly spending is $1,125,000. At $1,500 a month gets there in about 28 years. No inheritance, no lottery. Just a high savings rate doing quiet work.',
			placeholder: 'e.g. 65',
		},
		{
			type: 'estimate',
			id: 'rr-spend-lever',
			setup: 'Same Person A. They cut spending by $500 a month and reinvest the difference. They now invest $2,000 a month and spend $39,000 a year.',
			prompt: 'How many years EARLIER can Person A stop working?',
			unit: 'years',
			answer: 6.2,
			goodWithin: 1.3,
			zeroBeyond: 3,
			bias: 'rate_leverage_blindspot',
			biasDirection: 'under',
			reveal: 'About 6 years earlier, at 52. A spending cut works both sides at once: more money working for you every month, and a smaller yearly spend.',
			placeholder: 'e.g. 2',
		},
		{
			type: 'estimate',
			id: 'rr-late-start',
			setup: 'Person B is 45 with nothing invested. They invest $2,000 each month at a 5% return after inflation, and they spend $45,000 a year. Work becomes optional when investments reach 25 times their yearly spending.',
			prompt: 'At what age can Person B make work optional?',
			unit: 'years',
			answer: 69.2,
			goodWithin: 1.08,
			zeroBeyond: 1.35,
			bias: 'time_undervalue',
			biasDirection: 'under',
			reveal: 'About age 69. Starting at 45 with nothing invested. Person B needs $1,125,000, and $2,000 a month gets there in about 24 years.',
			placeholder: 'e.g. 75',
		},
		{
			type: 'estimate',
			id: 'rr-return-drag',
			setup: 'Back to the original Person A: 30, $1,500 a month, spending $45,000 a year. Now assume their return is 4% after inflation instead of 5%.',
			prompt: 'How many years LATER can Person A stop working?',
			unit: 'years',
			answer: 3,
			goodWithin: 1.35,
			zeroBeyond: 3.5,
			bias: 'rate_leverage_blindspot',
			biasDirection: 'under',
			reveal: 'About 3 years later. One percentage point of return, held for decades, moves a retirement date by years. This is the same force that makes a 1% fee so expensive over a lifetime.',
			placeholder: 'e.g. 1',
		},
		{
			type: 'estimate',
			id: 'rr-save-more',
			setup: 'Original Person A again, at the 5% return. This time they find an extra $250 a month to invest, and their spending stays at $45,000 a year.',
			prompt: 'How many years EARLIER can Person A stop working?',
			unit: 'years',
			answer: 2.2,
			goodWithin: 1.35,
			zeroBeyond: 3.5,
			bias: 'rate_leverage_blindspot',
			biasDirection: 'over',
			reveal: 'About 2 years earlier. Compare the levers: an extra $250 saved bought 2 years, while the $500 spending cut bought 6. The cut wins because it also shrinks the target Person A is chasing.',
			placeholder: 'e.g. 5',
		},
		{
			type: 'estimate',
			id: 'rr-coast',
			setup: 'Person C invests $2,000 each month from age 30 to age 40, then never adds another dollar. The balance keeps growing at a 5% return after inflation. Person C spends $45,000 a year.',
			prompt: 'At what age does Person C\u2019s balance reach the point where work is optional?',
			unit: 'years',
			answer: 65.8,
			goodWithin: 1.08,
			zeroBeyond: 1.4,
			bias: 'exponential_underestimate',
			biasDirection: 'over',
			reveal: 'Age 66. Ten hard years built $310,565 by 40, and growth alone carried it to $1,125,000 with zero further saving. The deposits stopped. The compounding never did.',
			placeholder: 'e.g. 75',
		},
	],

	resultBands: [
		{
			minHits: 5,
			title: 'Clock reader',
			insight:
				'You called the dates better than almost anyone. You already sense the thing most people miss: the retirement age is an output of a few inputs, not a fixed number on a government form. Nisk runs the same clock on your own inputs.',
		},
		{
			minHits: 4,
			title: 'Right ballpark',
			insight:
				'You landed near most of the dates and probably missed on the levers. The sizes are the surprising part: a $500 spending cut moved a date 6 years while $250 of extra saving moved it 2. The levers are where the plan actually lives.',
		},
		{
			minHits: 2,
			title: 'The date drifts on you',
			insight:
				'Most people are here. Retirement dates feel like fate, so guessing them feels like guessing the weather. They are not fate. Every date in this quiz came from three inputs: what goes in monthly, what goes out yearly, and the return. Change an input and the date moves.',
		},
		{
			minHits: 0,
			title: 'Guessing without the clock',
			insight:
				'Almost nobody can feel these dates, and your guesses just showed it honestly. That is not a flaw. It is the reason to stop estimating and start computing. Nisk turns your three inputs into your actual date in about a minute.',
		},
	],

	biasCopy: {
		exponential_underestimate: {
			label: 'Linear thinking',
			insight:
				'You picture balances growing in straight lines. Person C\u2019s money tripled after the deposits stopped, and straight-line guessing will always miss that.',
		},
		time_undervalue: {
			label: 'Time blindness',
			insight:
				'You weigh the dollars more than the years. Person B\u2019s late start still landed a normal retirement because 20 years is a long time, even when it does not feel like it.',
		},
		erosion_blindspot: {
			label: 'Erosion blindspot',
			insight:
				'You feel the growth side but not the drag side. Inflation and fees quietly push every retirement date later, one compounding year at a time.',
		},
		rate_leverage_blindspot: {
			label: 'Rate leverage',
			insight:
				'Small monthly changes read as small to you. In this quiz a $500 monthly change moved a retirement date by 6 years. Percentages and monthly amounts are the biggest levers on the clock.',
		},
	},

	cta: {
		label: 'Compute your real date',
		to: '/simulate',
		blurb: 'These were made-up people with clean numbers. Nisk runs the same clock on your actual income, spending, and savings, and shows which lever moves your date the most.',
	},

	shareText:
		'I scored {hits}/{total} on Nisk\u2019s Retirement Age Reality Check. A $500 monthly spending cut moved a retirement date 6 years. See if your gut can call the dates: https://nisk.co/quizzes/retirement-reality',
};
